import { logger } from '../../shared/utils/logger';

/**
 * Sprinklr Live Chat SDK loader.
 *
 * Injects the Sprinklr embed script using the pattern from the working
 *   <script src="${cdnHost}/sdk/${environment}/${appId}">
 *
 * The SDK announces readiness via a custom DOM event `@spr/CHAT_LOADED`.
 * After loading, the SDK instance lives at `window.sprinklr.chat`.
 */

const MAX_RETRIES = 3;

/**
 * Loads the Sprinklr Live Chat SDK script into the page.
 *
 * @param {string} cdnHost     – e.g. 'https://live-chat-static.sprinklr.com'
 * @param {string} environment – e.g. 'prod2'
 * @param {string} appId       – your Sprinklr App ID
 * @returns {Promise<void>} Resolves when the SDK is loaded.
 */
export function loadSprinklrSDK(cdnHost, environment, appId) {
  // Already loaded
  if (window.sprinklr?.chat?.loaded) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    // Set up the global scaffold the SDK expects
    window.sprinklr = window.sprinklr || {};
    let retry = 0;

    window.sprinklr.chat = {
      load() {
        return new Promise((res, rej) => {
          if (window.sprinklr.chat.loaded) {
            res();
            return;
          }

          const onEvent = ({ detail }) => {
            if (detail?.error) {
              rej(detail.error);
            } else {
              res();
            }
            document.removeEventListener('@spr/CHAT_LOADED', onEvent);
          };

          document.addEventListener('@spr/CHAT_LOADED', onEvent);

          const scriptUrl = `${cdnHost}/sdk/${environment}/${appId}`;
          const script = document.createElement('script');
          script.type = 'text/javascript';
          script.async = true;
          script.src = scriptUrl;

          script.onerror = () => {
            if (retry < MAX_RETRIES) {
              retry += 1;
              logger.warn(
                `[Sprinklr] SDK script load failed, retrying (${retry}/${MAX_RETRIES})…`,
              );
              setTimeout(() => {
                document.head.appendChild(script.cloneNode(true));
              }, 250);
            } else {
              rej(
                new Error(
                  `Failed to load Sprinklr SDK from ${scriptUrl} after ${MAX_RETRIES} retries`,
                ),
              );
            }
          };

          const firstScript = document.getElementsByTagName('script')[0];
          if (firstScript?.parentNode) {
            firstScript.parentNode.insertBefore(script, firstScript);
          } else {
            document.head.appendChild(script);
          }
        });
      },
    };

    window.sprinklr.chat
      .load()
      .then(() => {
        window.sprinklr.chat.loaded = true;
        logger.log('[Sprinklr] SDK script loaded successfully');
        resolve();
      })
      .catch((err) => {
        logger.error('[Sprinklr] SDK script failed to load:', err);
        reject(err);
      });
  });
}
